<template>
    <others>
        <div class="p-6 bg-white border-b border-gray-200 overflow-hidden shadow-sm sm:rounded-lg">
            <loading :showLoading="form.processing" />
            <div class="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div>
                    <p class="text-lg font-medium">University Marks</p>
                    <p class="text-sm text-gray-500">
                        {{ marking.course.title }} - {{ marking.course_year.title }} - {{ marking.semester.title }}
                    </p>
                </div>
                <div class="flex items-center gap-3">
                    <select class="border-gray-300 rounded-md shadow-sm" v-model="branchId" @change="changeBranch" v-if="branches.length">
                        <option v-for="branch in branches" :key="branch.id" :value="branch.id">
                            {{ branch.title }}
                        </option>
                    </select>
                    <breeze-button type="button" :disabled="reminderForm.processing || !branchId || internalMarksFilled" @click="sendReminder" v-if="branches.length">
                        Send Reminder
                    </breeze-button>
                    <a
                        v-if="branchMarksComplete"
                        :href="route('others.university-marking.export', {universityMarking: marking.id, branch_id: branchId})"
                        class="inline-flex items-center px-4 py-2 bg-gray-800 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700 active:bg-gray-900 focus:outline-none focus:border-gray-900 focus:shadow-outline-gray transition ease-in-out duration-150"
                    >
                        Download
                    </a>
                    <button-link :href="route('others.university-marking.list')">Back</button-link>
                </div>
            </div>

            <form @submit.prevent="submitForm">
                <div class="overflow-x-auto border border-gray-200 shadow sm:rounded-lg" v-if="rows.length">
                    <table class="w-full min-w-full divide-y divide-gray-200 whitespace-nowrap">
                        <thead class="bg-gray-50">
                        <tr>
                            <th class="sticky left-0 z-20 min-w-[240px] bg-gray-50 px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Student</th>
                            <th class="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Branch</th>
                            <th v-for="subject in subjects" :key="subject.id" class="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                {{ subject.subject.title }}
                                <div class="text-[11px] normal-case text-gray-400">I {{ subject.internal_max_marks }} / E {{ subject.external_max_marks }}</div>
                            </th>
                            <th class="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Total</th>
                        </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                        <tr v-for="row in rows" :key="row.admission_id">
                            <td class="sticky left-0 z-10 min-w-[240px] bg-white px-4 py-4 text-sm font-medium text-gray-900">
                                {{ row.roll_no }} - {{ row.student_name }}
                            </td>
                            <td class="px-4 py-4 text-sm text-gray-700">{{ row.branch_title }}</td>
                            <td v-for="mark in row.marks" :key="`${row.admission_id}-${mark.subject_id}`" class="px-4 py-4 text-sm">
                                <div class="flex gap-2">
                                    <input type="number" min="0" :max="mark.internal_max_marks" step="0.01" class="w-24 border-gray-300 rounded-md shadow-sm text-sm" v-model="mark.internal_marks" placeholder="Internal"/>
                                    <input type="number" min="0" :max="mark.external_max_marks" step="0.01" class="w-24 border-gray-300 rounded-md shadow-sm text-sm" v-model="mark.external_marks" placeholder="External"/>
                                </div>
                            </td>
                            <td class="px-4 py-4 text-sm text-gray-700">
                                {{ total(row, 'internal_marks') }} / {{ total(row, 'external_marks') }}
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
                <div class="py-4 overflow-hidden text-center border-b border-gray-200 shadow sm:rounded-lg" v-else>
                    No students found
                </div>

                <div class="flex items-center gap-4 mt-4" v-if="rows.length">
                    <breeze-button :disabled="form.processing">Submit Marks</breeze-button>
                    <span v-if="form.recentlySuccessful" class="text-green-600">Marks saved.</span>
                </div>
                <exception />
            </form>
        </div>
    </others>
</template>

<script>
import Others from '@/Pages/Others/Others'
import BreezeButton from '@/Components/Button'
import ButtonLink from '@/Components/ButtonLink'
import Loading from '@/Components/Loading'
import Exception from '@/Components/Exception'
import {useForm} from '@inertiajs/inertia-vue3'

export default {
    components: {
        Others,
        BreezeButton,
        ButtonLink,
        Loading,
        Exception,
    },
    props: ['marking', 'branches', 'selectedBranchId', 'branchMarksComplete', 'subjects', 'rows'],
    data() {
        return {
            branchId: this.selectedBranchId ?? '',
            form: useForm({
                marks: [],
            }),
            reminderForm: useForm({
                branch_id: this.selectedBranchId ?? '',
            }),
        }
    },
    computed: {
        internalMarksFilled() {
            return this.rows.length > 0 && this.rows.every((row) => {
                return row.marks.length > 0 && row.marks.every((mark) => {
                    return mark.internal_marks !== null && mark.internal_marks !== undefined && mark.internal_marks !== '';
                });
            });
        },
    },
    methods: {
        changeBranch() {
            this.$inertia.get(route('others.university-marking.marks', this.marking.id), {
                branch_id: this.branchId,
            });
        },
        sendReminder() {
            this.reminderForm.branch_id = this.branchId;
            this.reminderForm.post(route('others.university-marking.reminder', this.marking.id), {
                preserveScroll: true,
            });
        },
        submitForm() {
            this.form.marks = this.rows.flatMap((row) => row.marks.map((mark) => ({
                admission_id: row.admission_id,
                subject_id: mark.subject_id,
                internal_marks: mark.internal_marks,
                external_marks: mark.external_marks,
            })));
            this.form.put(route('others.university-marking.marks.update', this.marking.id), {
                preserveScroll: true,
            });
        },
        total(row, key) {
            return row.marks.reduce((sum, mark) => sum + Number(mark[key] || 0), 0).toFixed(2);
        },
    },
}
</script>
